import request from './request'

export type RefundTaxMode = 'exclusive_of_tax' | 'inclusive_of_tax' | 'out_of_scope'

export interface ApiResponse<T = unknown> {
  code: number
  msg: string
  data: T
}

export interface CareOrderRefundMetaOption {
  value: string
  label: string
}

export interface CareOrderRefundMetaData {
  tax_modes?: Array<{ value: string; label: string } | string>
  tax_rate?: number | string
}

export interface CareOrderRefundContextData {
  amount_paid?: string | number
  refundable_amount?: string | number
  refunded_amount?: string | number
  tax_rate?: number | string
  [key: string]: unknown
}

export interface CareOrderRefundPreviewData {
  /** 新接口优先使用 */
  total_text?: string
  total_amount?: string | number
  tax_amount?: string | number
  subtotal_amount?: string | number
  refundable_amount?: string | number
  can_submit?: boolean
  tax_type?: string
  tax_type_text?: string
  /** 旧字段兼容 */
  total_display?: string
  total?: string | number
  refund_tax?: string | number
  tax?: string | number
  [key: string]: unknown
}

export interface CareOrderRefundTargetParams {
  id: number
  line_index: number
}

export interface CareOrderRefundPreviewParams extends CareOrderRefundTargetParams {
  amount: number
  tax_type: RefundTaxMode
}

export interface CareOrderRefundSubmitParams extends CareOrderRefundPreviewParams {
  note?: string
}

export const DEFAULT_REFUND_TAX_OPTIONS: Array<{ value: RefundTaxMode; label: string }> = [
  { value: 'exclusive_of_tax', label: 'Exclusive of Tax' },
  { value: 'inclusive_of_tax', label: 'Inclusive of Tax' },
  { value: 'out_of_scope', label: 'Out of Scope (0%)' },
]

const KNOWN_TAX_MODES = new Set<RefundTaxMode>([
  'exclusive_of_tax',
  'inclusive_of_tax',
  'out_of_scope',
])

function asRefundTaxMode(raw: string): RefundTaxMode | null {
  const v = raw.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (KNOWN_TAX_MODES.has(v as RefundTaxMode)) return v as RefundTaxMode
  // 兼容 meta / 旧值 exclusive | inclusive
  if (v === 'exclusive' || v.includes('exclusive')) return 'exclusive_of_tax'
  if (v === 'inclusive' || v.includes('inclusive')) return 'inclusive_of_tax'
  if (v.includes('out_of_scope') || v.includes('out of scope') || v === '0') return 'out_of_scope'
  return null
}

export function buildRefundTaxOptions(
  meta: CareOrderRefundMetaData | null | undefined,
): Array<{ value: RefundTaxMode; label: string }> {
  const raw = meta?.tax_modes
  if (!Array.isArray(raw) || !raw.length) return [...DEFAULT_REFUND_TAX_OPTIONS]

  const options: Array<{ value: RefundTaxMode; label: string }> = []
  for (const entry of raw) {
    if (typeof entry === 'string') {
      const value = asRefundTaxMode(entry)
      if (!value) continue
      const fallback = DEFAULT_REFUND_TAX_OPTIONS.find((o) => o.value === value)
      options.push({ value, label: fallback?.label ?? entry })
      continue
    }
    const value = asRefundTaxMode(String(entry.value ?? ''))
    if (!value) continue
    const label = String(entry.label ?? '').trim()
    options.push({
      value,
      label: label || DEFAULT_REFUND_TAX_OPTIONS.find((o) => o.value === value)?.label || value,
    })
  }
  return options.length ? options : [...DEFAULT_REFUND_TAX_OPTIONS]
}

export function formatRefundMoney(raw: string | number | null | undefined): string {
  if (raw == null || raw === '') return '$0.00'
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (!s) return '$0.00'
    if (s.startsWith('$')) return s
    const n = Number(s.replace(/[^0-9.-]/g, ''))
    if (!Number.isFinite(n)) return s
    return `$${n.toFixed(2)}`
  }
  if (!Number.isFinite(raw)) return '$0.00'
  return `$${raw.toFixed(2)}`
}

export function parseRefundTaxRate(raw: number | string | null | undefined): number {
  if (raw == null || raw === '') return 0
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(/[^0-9.-]/g, ''))
  if (!Number.isFinite(n) || n < 0) return 0
  // 后端偶发返回 13 表示 13%
  if (n > 1) return n / 100
  return n
}

/** POST `/api/admin/care-order/refund-meta` */
export async function postCareOrderRefundMeta(
  params: Partial<CareOrderRefundTargetParams> = {},
): Promise<ApiResponse<CareOrderRefundMetaData>> {
  return request.post<ApiResponse<CareOrderRefundMetaData>>(
    '/api/admin/care-order/refund-meta',
    params,
  )
}

/** POST `/api/admin/care-order/refund-context` */
export async function postCareOrderRefundContext(
  params: CareOrderRefundTargetParams,
): Promise<ApiResponse<CareOrderRefundContextData>> {
  return request.post<ApiResponse<CareOrderRefundContextData>>(
    '/api/admin/care-order/refund-context',
    params,
  )
}

/** POST `/api/admin/care-order/refund-preview` */
export async function postCareOrderRefundPreview(
  params: CareOrderRefundPreviewParams,
): Promise<ApiResponse<CareOrderRefundPreviewData>> {
  return request.post<ApiResponse<CareOrderRefundPreviewData>>(
    '/api/admin/care-order/refund-preview',
    params,
  )
}

/** POST `/api/admin/care-order/refund` */
export async function postCareOrderRefund(
  params: CareOrderRefundSubmitParams,
): Promise<ApiResponse<unknown>> {
  return request.post<ApiResponse<unknown>>('/api/admin/care-order/refund', params)
}
